import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { IDropdownItem } from '../../../../ui';
import { IReactionDisposer, reaction } from 'mobx';
import { PoiOverviewNoResults } from './PoiOverviewNoResults';
import { PoiList } from './list';
import { PoiListShimmer } from './list/shimmer';
import { KeywordFilter } from './filter';
import { PoiSearchBar } from './search';
import {
	EVENT_ACTION_POI_AMENITY_KEYWORD_CHANGE,
	EVENT_ACTION_POI_AREA_CHANGE,
	EVENT_ACTION_POI_KEYWORD_CHANGE,
	EVENT_ACTION_POI_QUERY_CHANGE,
	EVENT_ACTION_POI_SHOW_MORE,
	EVENT_CATEGORY_POI,
	EVENT_LABEL_POI_AREA_ALL,
	EVENT_LABEL_POI_KEYWORD_ALL,
	trackCustomEvent,
} from '../../../shared/analytics';
import { ShowMoreButton, SolidContentWrap } from '../../../shared/components';
import { Keyword } from '../../domain/models';
import { Amenity, isAmenity } from '../../domain/models/Amenity';
import { PoiAreaStore, PoiListStore } from '../../domain/stores';
import { AmenityFilter } from './filter/AmenityFilter';
import { getKeywordForHash } from './helpers/getKeywordForHash';
import { setHashByKeyword } from './helpers/setHashByKeyword';
import { isServerSide } from '../../../shared/utils/isServerSide';
import { IStores } from '../../../app/root/domain/stores/createStores';
import { getCurrentHash } from '../../../shared/router/utils/getCurrentHash';
import { getCurrentSearch } from '../../../shared/router/utils/getCurrentSearch';
import { VisuallyHidden } from '../../../../ui/components/VisuallyHidden/VisuallyHidden';
import { useTranslation } from '../../../shared/i18n/translate/useTranslation';

interface IProps {}

interface IInjectedProps extends IProps {
	poiListStore: PoiListStore;
	poiAreaStore: PoiAreaStore;
}

const PoiResultsAnnouncer = ({ count }: { count: number | null }) => {
	const { t } = useTranslation();
	return (
		<VisuallyHidden
			as="div"
			aria-live="polite"
			aria-atomic="true"
			role="status"
		>
			{count !== null ? t('poi_filter_results_count', { total: count }) : ''}
		</VisuallyHidden>
	);
};

export const PoiOverview = inject(
	({ poiListStore, poiAreaStore }: IStores) => ({
		poiListStore,
		poiAreaStore,
	}),
)(
	observer(
		class PoiOverviewWithRouteData extends Component<IProps> {
			private disposeQueryReaction: IReactionDisposer;

			private disposeResultsReaction: IReactionDisposer;

			constructor(props: IProps) {
				super(props);
				this.updateFilterByHash();

				this.disposeQueryReaction = reaction(
					() => this.injected.poiListStore.currentQuery,
					this.onChangeQuery,
					{ delay: 1000 },
				);

				this.disposeResultsReaction = reaction(
					() => this.injected.poiListStore.results,
					this.onChangeResults,
					{ delay: 1000 },
				);
			}

			protected get injected() {
				return this.props as IInjectedProps;
			}

			public componentDidMount() {
				if (!isServerSide()) {
					window.scrollTo(0, 0);

					this.updateFilterByQuery();
				}
			}

			public componentWillUnmount() {
				this.disposeQueryReaction();
				this.disposeResultsReaction();

				this.injected.poiListStore.reset();
			}

			private updateFilterByHash() {
				const hash = getCurrentHash();
				const keyword = hash ? getKeywordForHash(hash) : undefined;
				this.injected.poiListStore.setKeywordFilter(keyword);
			}

			private updateFilterByQuery() {
				const filters = getCurrentSearch()?.get('filters')?.split(',');
				const amenityFilters: Amenity[] = [];

				filters?.forEach((filter) => {
					const formattedFilter = filter.replace('-', ' ');
					if (isAmenity(formattedFilter)) {
						amenityFilters.push(formattedFilter);
					}
				});

				this.injected.poiListStore.setAmenityKeywords(amenityFilters);
			}

			private onClickShowMore = () => {
				this.injected.poiListStore.showMore();

				trackCustomEvent({
					category: EVENT_CATEGORY_POI,
					action: EVENT_ACTION_POI_SHOW_MORE,
				});
			};

			private onChangeKeyword = (keyword?: Keyword) => {
				setHashByKeyword(keyword);
				this.injected.poiListStore.setKeywordFilter(keyword);

				const eventLabel = keyword || EVENT_LABEL_POI_KEYWORD_ALL;
				trackCustomEvent({
					category: EVENT_CATEGORY_POI,
					action: EVENT_ACTION_POI_KEYWORD_CHANGE,
					label: eventLabel,
				});
			};

			private onToggleAmenity = (amenityKeyword: Amenity) => {
				const { poiListStore } = this.injected;
				poiListStore.toggleAmenityKeyword(amenityKeyword);
				trackCustomEvent({
					category: EVENT_CATEGORY_POI,
					action: EVENT_ACTION_POI_AMENITY_KEYWORD_CHANGE,
					label: poiListStore.currentAmenityKeywords.join(','),
				});
			};

			private onChangeArea = (item: IDropdownItem | null) => {
				const area = typeof item === 'string' ? item : undefined;
				this.injected.poiListStore.setAreaFilter(area);

				const eventLabel = area || EVENT_LABEL_POI_AREA_ALL;
				trackCustomEvent({
					category: EVENT_CATEGORY_POI,
					action: EVENT_ACTION_POI_AREA_CHANGE,
					label: eventLabel,
				});
			};

			private onUpdateQuery = (query: string) => {
				this.injected.poiListStore.setQuery(query);
			};

			private onChangeQuery = () => {
				const { currentQuery } = this.injected.poiListStore;

				if (!currentQuery) {
					return;
				}

				trackCustomEvent({
					category: EVENT_CATEGORY_POI,
					action: EVENT_ACTION_POI_QUERY_CHANGE,
					label: currentQuery,
				});
			};

			private onChangeResults = () => {
				const { currentArea, currentKeyword, currentQuery, sortedResults } =
					this.injected.poiListStore;

				const eventLabels = [
					currentArea || EVENT_LABEL_POI_AREA_ALL,
					currentKeyword || EVENT_LABEL_POI_KEYWORD_ALL,
				];

				if (currentQuery) {
					eventLabels.push(currentQuery);
				}

				trackCustomEvent({
					category: EVENT_CATEGORY_POI,
					action: EVENT_ACTION_POI_QUERY_CHANGE,
					label: eventLabels.join('/'),
					value: sortedResults.length,
				});
			};

			private renderList() {
				const {
					isLoading,
					finishedInitialLoad,
					results,
					previousShowUntilIndex,
				} = this.injected.poiListStore;

				if (isLoading || !finishedInitialLoad) {
					return <PoiListShimmer listLength={PoiListStore.INITIAL_LENGTH} />;
				}

				if (!results || !results.length) {
					return <PoiOverviewNoResults />;
				}

				return (
					<PoiList results={results} previousIndex={previousShowUntilIndex} />
				);
			}

			public render() {
				const {
					isLoading,
					finishedInitialLoad,
					hasMore,
					currentKeyword,
					currentAmenityKeywords,
					sortedResults,
					currentQuery,
				} = this.injected.poiListStore;
				const { areas } = this.injected.poiAreaStore;

				const isReady = !isLoading && finishedInitialLoad;

				return (
					<SolidContentWrap>
						<PoiSearchBar
							areas={areas}
							disabled={!finishedInitialLoad}
							onChangeArea={this.onChangeArea}
							onUpdateQuery={this.onUpdateQuery}
						/>
						<PoiResultsAnnouncer
							count={currentQuery ? sortedResults.length : null}
						/>

						<KeywordFilter
							disabled={!finishedInitialLoad}
							// eslint-disable-next-line @typescript-eslint/ban-ts-comment
							// @ts-ignore
							currentKeyword={currentKeyword}
							onChangeKeyword={this.onChangeKeyword}
						/>

						<AmenityFilter
							currentAmenities={currentAmenityKeywords}
							onToggleAmenity={this.onToggleAmenity}
						/>

						{this.renderList()}

						{isReady && hasMore && (
							<ShowMoreButton onClick={this.onClickShowMore} />
						)}
					</SolidContentWrap>
				);
			}
		},
	),
);
